import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecoveryAnalytics = ({ analytics, gymId }) => {
  const analyticsCards = [
    {
      label: 'Total Failed Payments',
      value: analytics?.total_failed_payments || 0,
      icon: 'AlertCircle',
      color: 'red',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600'
    },
    {
      label: 'Total Failed Amount',
      value: `$${analytics?.total_failed_amount?.toFixed(2) || '0.00'}`,
      icon: 'DollarSign',
      color: 'red',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600'
    },
    {
      label: 'Successful Recoveries',
      value: analytics?.successful_recoveries || 0,
      icon: 'CheckCircle',
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      label: 'Recovered Amount',
      value: `$${analytics?.recovered_amount?.toFixed(2) || '0.00'}`,
      icon: 'TrendingUp',
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      label: 'Recovery Rate',
      value: `${analytics?.recovery_rate?.toFixed(1) || 0}%`,
      icon: 'Percent',
      color: 'blue',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      label: 'Avg Retry Attempts',
      value: analytics?.avg_retry_attempts?.toFixed(1) || '0.0',
      icon: 'RefreshCw',
      color: 'purple',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    {
      label: 'Dunning Notifications Sent',
      value: analytics?.dunning_notifications_sent || 0,
      icon: 'Send',
      color: 'orange',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    {
      label: 'Dunning Response Rate',
      value: `${analytics?.dunning_response_rate?.toFixed(1) || 0}%`,
      icon: 'MessageCircle',
      color: 'orange',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    {
      label: 'Churn Prevented',
      value: analytics?.churn_prevented_count || 0,
      icon: 'Shield',
      color: 'teal',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600'
    },
    {
      label: 'Churn Prevention Value',
      value: `$${analytics?.churn_prevented_value?.toFixed(2) || '0.00'}`,
      icon: 'Award',
      color: 'teal',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600'
    }
  ];

  const recoveryEffectiveness = analytics?.total_failed_payments > 0
    ? ((analytics?.successful_recoveries / analytics?.total_failed_payments) * 100)?.toFixed(1)
    : 0;

  const dunningEffectiveness = analytics?.dunning_notifications_sent > 0
    ? analytics?.dunning_response_rate?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Recovery Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {analyticsCards?.map((card) => (
            <div key={card?.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{card?.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{card?.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${card?.bgColor} flex items-center justify-center`}>
                  <Icon name={card?.icon} className={`w-6 h-6 ${card?.iconColor}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="Target" className="w-5 h-5 text-blue-600" />
            Recovery Effectiveness
          </h4>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Overall Recovery Rate</span>
                <span className="text-sm font-semibold text-gray-900">{recoveryEffectiveness}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${recoveryEffectiveness}%` }}
                ></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <p className="text-xs text-gray-600">Failed</p>
                <p className="text-lg font-bold text-red-600">
                  {analytics?.total_failed_payments - analytics?.successful_recoveries || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Recovered</p>
                <p className="text-lg font-bold text-green-600">
                  {analytics?.successful_recoveries || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="MessageSquare" className="w-5 h-5 text-orange-600" />
            Dunning Effectiveness
          </h4>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Response Rate</span>
                <span className="text-sm font-semibold text-gray-900">{dunningEffectiveness}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${dunningEffectiveness}%` }}
                ></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <p className="text-xs text-gray-600">Sent</p>
                <p className="text-lg font-bold text-blue-600">
                  {analytics?.dunning_notifications_sent || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Responded</p>
                <p className="text-lg font-bold text-green-600">
                  {Math.round((analytics?.dunning_notifications_sent * analytics?.dunning_response_rate / 100) || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Impact */}
      <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-green-200 p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="DollarSign" className="w-5 h-5 text-green-600" />
          Financial Impact Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Total at Risk</p>
            <p className="text-3xl font-bold text-red-600">
              ${analytics?.total_failed_amount?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Amount Recovered</p>
            <p className="text-3xl font-bold text-green-600">
              ${analytics?.recovered_amount?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Churn Prevention Value</p>
            <p className="text-3xl font-bold text-teal-600">
              ${analytics?.churn_prevented_value?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-green-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total Value Preserved</span>
            <span className="text-2xl font-bold text-green-700">
              ${((analytics?.recovered_amount || 0) + (analytics?.churn_prevented_value || 0))?.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Analytics data for current period. Export for detailed reporting.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" iconName="Download">
            Export CSV
          </Button>
          <Button variant="outline" iconName="FileText">
            Generate Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecoveryAnalytics;